import { BaseHTMLAttributes, FC, ForwardRefExoticComponent, RefAttributes } from "react";
import { Logo } from "./logo";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import clsx from "clsx";
import { CircleAlert, LucideProps } from "lucide-react";
import FlagComponent, { CurrencyCode } from "./flag/flag";

interface EntityImageProps extends Pick<BaseHTMLAttributes<HTMLElement>, 'className'> {
    /**
     * @default "logo"
     */
    type?: "logo" | "avatar" | 'currency' | 'icon';

    /**
     * @default "M"
     */
    size?: "S" | "M" | "L" | 'XL' | 'XXL';
    
    source?: string | CurrencyCode | ForwardRefExoticComponent<
        Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
    >;
    fallback?: string;

    /**
     * @default "rounded-full"
     */
    rounded?: 'rounded-full' | 'rounded-sm' | 'rounded-md' | 'rounded-lg' | 'rounded-xl' | 'rounded-2xl';
  }

const EntityImage: FC<EntityImageProps> = ({ type = 'logo', source, size = 'M', fallback, className, rounded = 'rounded-full' }) => {
    if (type === 'logo' && typeof source === 'string') {
        return (
            <Logo url={source} shortName={fallback} className={clsx(rounded, className)} />
        );
    } else if (type === 'avatar' && typeof source === 'string') {
        return (
            <Avatar className={clsx("w-10 h-10 p-0.5", rounded, className)}>
                <AvatarImage className={rounded} src={source} />
                <AvatarFallback>{fallback ? fallback?.slice(0, 2).toUpperCase() : 'NA'}</AvatarFallback>
            </Avatar>
        );
    } else if (type === 'currency') {
        return <FlagComponent currencyCode={source as CurrencyCode} size={size} />
    } else if (type == 'icon' && source) {
        const IconComponent = source;
        return <IconComponent size={
            size === 'S' ? 16 : 
            size === 'M' ? 24 : 
            size === 'L' ? 32 : 16
        } className={clsx(rounded, className)} />
    } else {
        return <div className={clsx("flex w-10 h-10 bg-foreground/10 items-center", rounded)}>
            <CircleAlert className="mx-auto" />
        </div>
    }
}

export { EntityImage };